import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Flex,
  SearchOption,
  TypeaheadInput,
  AgGrid,
  AgGridReact,
  TypeaheadInputComponent,
} from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { TextWithIcon } from "../../TextWithIcon";
import { filterText } from "../styles_vanilla.css";

export type CustomFilterParams = {
  /**
   * Field name of another column whose filter model gates this filter. When
   * omitted, no gate is applied and the filter is enabled from mount.
   */
  dependentFilter?: string;
};

export type CustomFilterProps = {
  dependentFilter?: string;
  options: SearchOption[];
  isLoading: boolean;
  infoText: string;
  hintText: string;
  filterProps: AgGridReact.CustomFilterProps;
  optionsFetcher: (dependentId: string, searchTerm: string) => void;
  onSelection?: (dependentId: string, selection: string) => void;
};

/**
 * When a filter is dependent on the other filter's availability, this component abstracts out the base logic
 * to support that functionality.
 * Usage example: {@link EntityFilter}, {@link AccountFilter}
 * @param filterProps props fed by the AgGrid filtering model
 */
export const CustomFilter = (props: CustomFilterProps) => {
  const {
    dependentFilter,
    filterProps,
    options,
    isLoading,
    infoText,
    hintText,
    optionsFetcher,
    onSelection,
  } = props;
  const { api, getValue, onModelChange } = filterProps;

  const [dependentId, setDependentId] = useState<string>(
    dependentFilter ? api.getColumnFilterModel(dependentFilter) || "" : "",
  );
  const filterEnabled = !dependentFilter || dependentId?.length > 0;

  // Ref to always hold the latest dependentId, avoiding stale closures
  // during AG Grid's synchronous filterChanged event cascades.
  const dependentIdRef = useRef(dependentId);

  const componentRef = useRef<TypeaheadInputComponent>(null);

  useEffect(() => {
    componentRef?.current?.clearInputValue();
  }, [dependentId]);

  const handleSelection = useCallback(
    (value?: SearchOption) => {
      if (onSelection) {
        onSelection(dependentIdRef.current, value?.id as string);
      } else {
        onModelChange(value?.id);
      }
    },
    [onModelChange, onSelection],
  );

  const handleDebouncedSearch = useCallback(
    (value?: string) => {
      if (value?.length) {
        optionsFetcher(dependentIdRef.current, value);
      } else {
        onModelChange(null);
      }
    },
    [onModelChange, optionsFetcher],
  );

  const onAnyFilterChanged = useCallback(() => {
    // When there is no dependent filter the reset cascade doesn't apply,
    // and reading a non-existent column's model is meaningless. Skip it.
    if (!dependentFilter) return;
    const newId: string = api.getColumnFilterModel(dependentFilter) || "";
    if (newId != dependentIdRef.current) {
      onModelChange(null);
      // Update ref synchronously so subsequent filter cascade callbacks
      // within the same tick see the latest value.
      dependentIdRef.current = newId;
      setDependentId(newId);
    }
  }, [api, dependentFilter, onModelChange]);

  const doesFilterPass = useCallback(
    (params: AgGrid.IDoesFilterPassParams) => {
      return getValue(params.node) == filterProps.model;
    },
    [filterProps, getValue],
  );

  // register filter callbacks with the grid
  AgGridReact.useGridFilter({ doesFilterPass, onAnyFilterChanged });

  return (
    <Flex flexDirection="column" gap={Sizes.SM}>
      {!filterEnabled && <TextWithIcon label={infoText} iconType={"exclamation-circle"} />}
      <TypeaheadInput
        className={filterText}
        ref={componentRef}
        isClearable
        isFullWidth
        disabled={!filterEnabled}
        isLoading={isLoading}
        options={options}
        onSelectOption={handleSelection}
        onDebouncedSearch={handleDebouncedSearch}
        placeholder={hintText}
      />
    </Flex>
  );
};
